<template>
    <div>
        <div class="mt-4 d-flex align-center justify-space-between">
            <div>
                <h2 class="text-h6">
                    <strong>{{ template.conditions.length ? 'Conditional Messages' : 'Message Settings' }}</strong>
                </h2>

                <div
                    v-if="template.conditions.length > 1"
                    class="text-caption text--secondary mb-2 d-flex align-center"
                >
                    <span>
                        Conditions are evaluated in order from top to bottom. The first matching condition determines which message is sent.
                    </span>
                </div>
            </div>

            <div v-if="supports_conditions" class="text-right">
                <v-btn
                    color="primary"
                    :disabled="read_only"
                    outlined
                    small
                    @click="onClickAddConditionMessage"
                >
                    Add Conditional Message
                </v-btn>
            </div>
        </div>

        <m-auto-message-condition-edit-dialog
            ref="condition-edit-dialog"
            @save="onEditConditionSave"
        />

        <v-expansion-panels v-if="template.conditions.length > 0" class="mt-2 mb-8" flat>
            <v-expansion-panel
                v-for="(conditional, index) in template.conditions"
                :key="'panel-'+index"
                class="mt-1"
            >
                <v-expansion-panel-header class="shaded-light">
                    <strong>
                        <span v-html="conditional.description"/>
                    </strong>
                </v-expansion-panel-header>

                <v-expansion-panel-content class="expansion-content pt-4">
                    <m-auto-message-message-ctrls
                        :send_from_choices="send_from_choices"
                        :link_shortener_host_choices="link_shortener_host_choices"
                        :message_pattern_context="template.template_context"
                        :template_subject_id="template.template_subject_id"
                        :value="conditional"
                        :disable_mode="organization_account != null && organization_account.is_sandbox_organization"
                        :read_only="read_only"
                        @input="onChangeConditionMessageCtrls"
                    />

                    <div class="text-right d-flex justify-end flex-wrap gap-3 mt-1">
                        <v-btn
                            v-if="conditional.condition_index > 0"
                            :disabled="read_only"
                            @click="onClickMoveConditionalUp(conditional)"
                        >
                            Move Up...
                        </v-btn>

                        <v-btn
                            v-if="conditional.condition_index+1 <= template.conditions.length-1"
                            :disabled="read_only"
                            @click="onClickMoveConditionalDown(conditional)"
                        >
                            Move Down...
                        </v-btn>

                        <v-btn
                            :disabled="read_only"
                            @click="onClickDeleteConditional(conditional)"
                        >
                            Delete this conditional message...
                        </v-btn>

                        <v-btn
                            :disabled="read_only"
                            @click="onClickEditConditions(conditional)"
                        >
                            Edit Conditions...
                        </v-btn>
                    </div>
                </v-expansion-panel-content>

            </v-expansion-panel>

            <v-expansion-panel class="mt-1">
                <v-expansion-panel-header class="shaded-light"><strong>Otherwise (i.e. if none of the above conditions match)</strong></v-expansion-panel-header>
                <v-expansion-panel-content class="expansion-content pt-4">
                    <m-auto-message-message-ctrls
                        :send_from_choices="send_from_choices"
                        :link_shortener_host_choices="link_shortener_host_choices"
                        :message_pattern_context="template.template_context"
                        :template_subject_id="template.template_subject_id"
                        :value="template"
                        @input="onChangeDefaultMessageCtrls"
                        :disable_mode="organization_account != null && organization_account.is_sandbox_organization"
                        :read_only="read_only"
                        default_message_control
                    />
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <div v-else>
            <m-auto-message-message-ctrls
                :send_from_choices="send_from_choices"
                :link_shortener_host_choices="link_shortener_host_choices"
                :message_pattern_context="template.template_context"
                :template_subject_id="template.template_subject_id"
                :value="template"
                :disable_mode="organization_account != null && organization_account.is_sandbox_organization"
                :read_only="read_only"
                default_message_control
                is_top_level
                @input="onChangeDefaultMessageCtrls"
            />
        </div>
    </div>
</template>

<script>
export default {
    props: {
        template: {
            type: Object,
            required: true
        },
        supports_conditions: {
            type: Boolean,
            default: false
        },
        condition_profiles: {
            type: Array,
            default: () => []
        },
        integration_crm_summaries: {
            type: Array,
            default: () => []
        },
        send_from_choices: {
            type: Array,
            default: () => []
        },
        link_shortener_host_choices: {
            type: Array,
            default: () => []
        },
        organization_account: {
            type: Object,
            default: null
        },
        read_only: {
            type: Boolean,
            default: false
        }
    },

    methods: {
        onClickAddConditionMessage() {
            const condition = {
                label: 'Conditional message',
                constraints: [],
                send_mode: 'manual',
                send_from: this.template.send_from,
                message_patterns: this.template.message_patterns,
                condition_index: -1,
            };

            this.$refs['condition-edit-dialog'].showDialog(condition, this.condition_profiles, this.integration_crm_summaries, this.template.template_context);
        },

        onEditConditionSave(condition) {
            this.$emit('save', condition);
        },

        onChangeConditionMessageCtrls(newValue) {
            this.$emit('update-condition', newValue);
        },

        onChangeDefaultMessageCtrls(newValue) {
            this.$emit('update-default', newValue);
        },

        onClickMoveConditionalUp(conditional) {
            this.$emit('move-up', conditional);
        },

        onClickMoveConditionalDown(conditional) {
            this.$emit('move-down', conditional);
        },

        onClickDeleteConditional(conditional) {
            this.$emit('delete', conditional);
        },

        onClickEditConditions(conditional) {
            this.$refs['condition-edit-dialog'].showDialog(conditional, this.condition_profiles, this.integration_crm_summaries);
        }
    }
}
</script>

<style lang="scss" scoped>
.expansion-content {
    border: 1px solid var(--color-bg-shaded);
}
</style>
